// src/services/imageGenService.js
// Motor compartido del Generador de Imágenes del panel de administrador:
// bibliotecas de fondos en Supabase Storage, generación de fondo con IA
// (Pollinations.ai), extracción de datos de ofertas externas, y la
// composición final vía <canvas> -- tarjetas sólidas de marca superpuestas
// sobre el fondo (foto generada, foto propia del admin, o un degradado
// simple si no hay ninguna imagen disponible).
import { supabase } from './supabase';

const BUCKET = 'image-generator';
const EXTRACT_OFFER_URL = '/.netlify/functions/extract-offer';

export const FORMATS = {
  square:   { label: 'Cuadrado (feed)',        width: 1080, height: 1080 },
  vertical: { label: 'Vertical (stories/reels)', width: 1080, height: 1920 },
};

// ── Bibliotecas de fondos propios (Supabase Storage) ──────────────────
// Alternativa manual a la generación con IA -- el admin puede subir sus
// propias fotos si prefiere no usar el fondo generado.
export const misServiciosFolder = (service) => `mis-servicios/${service}`;
export const ofertasExternasFolder = () => 'ofertas-externas';

export async function listTemplates(folder) {
  const { data, error } = await supabase.storage.from(BUCKET).list(folder, {
    sortBy: { column: 'created_at', order: 'desc' },
  });
  if (error || !data) return [];
  return data
    .filter((f) => f.id) // ignora placeholders de carpeta
    .map((f) => {
      const path = `${folder}/${f.name}`;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      return { path, name: f.name, url: pub.publicUrl, createdAt: f.created_at };
    });
}

export async function uploadTemplate(folder, file) {
  const blob = await resizeImageToBlob(file, 1600, 0.88);
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
  const path = `${folder}/${filename}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    cacheControl: '3600',
  });
  if (error) throw error;
  const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return { path, name: filename, url: pub.publicUrl };
}

export async function deleteTemplate(path) {
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) throw error;
}

// ── Generación de fondo con IA (Pollinations.ai -- gratis, sin API key) ──
// Un fondo nuevo por cada llamada (seed aleatorio); "Generar otro fondo" en
// la UI simplemente vuelve a llamar a generateAiBackgroundUrl con el mismo
// prompt. Se pide siempre en formato vertical (más alto) porque de ahí se
// puede recortar tanto el cuadrado como el vertical con "cover", sin pedirle
// dos imágenes distintas a Pollinations por la misma sesión de generación.
const POLLINATIONS_BASE = 'https://image.pollinations.ai/prompt/';
const NO_TEXT_INSTRUCTION = 'sin texto, sin letras, sin numeros, sin palabras, no text, no words, no letters, no numbers';

const SERVICE_PROMPTS = {
  remesas: `bandera cubana ondeando junto al Capitolio de La Habana, arquitectura colonial cubana, cielo azul brillante, fotografia profesional fotorrealista, espacio despejado en la parte inferior de la imagen, ${NO_TEXT_INSTRUCTION}`,
  cambio_divisas: `billetes de dolar y de real brasileno junto a monedas sobre una mesa elegante, luz calida, fotografia profesional fotorrealista, espacio despejado en la parte inferior de la imagen, ${NO_TEXT_INSTRUCTION}`,
  recargas: `telefono movil moderno con senal de red brillante, ambiente urbano cubano de fondo, luces suaves, fotografia profesional fotorrealista, espacio despejado en la parte inferior de la imagen, ${NO_TEXT_INSTRUCTION}`,
  traducciones: `documentos oficiales y una pluma elegante sobre un escritorio de oficina, luz calida profesional, fotografia fotorrealista, espacio despejado en la parte inferior de la imagen, ${NO_TEXT_INSTRUCTION}`,
  documentos_cuba: `edificio gubernamental de arquitectura cubana clasica, banderas de cuba y brasil, cielo despejado, fotografia profesional fotorrealista, espacio despejado en la parte inferior de la imagen, ${NO_TEXT_INSTRUCTION}`,
  vuelos: `avion comercial despegando al atardecer, cielo dramatico con nubes doradas, pista de aeropuerto, fotografia aerea profesional fotorrealista, espacio despejado para texto, ${NO_TEXT_INSTRUCTION}`,
  oferta_externa: `avion comercial moderno volando sobre nubes doradas al atardecer, cielo espectacular, skyline de ciudad al fondo, fotografia aerea premium fotorrealista, espacio despejado para texto, ${NO_TEXT_INSTRUCTION}`,
};

// Heurística simple para enriquecer el prompt de Vuelos/Ofertas Externas con
// el destino cuando se puede detectar (reutiliza el mismo patrón "X-Y" que
// extractOfferFromText). No inventa un destino si no lo encuentra.
export function guessDestinationFromFreeText(text) {
  const m = String(text || '').match(/([A-ZÁÉÍÓÚÑ]{3,}(?:\s[A-ZÁÉÍÓÚÑ]{3,})*)\s*[-–—]\s*([A-ZÁÉÍÓÚÑ]{3,}(?:\s[A-ZÁÉÍÓÚÑ]{3,})*)/);
  return m ? m[2].trim() : null;
}

export function buildBackgroundPrompt(service, context = {}) {
  const base = SERVICE_PROMPTS[service] || SERVICE_PROMPTS.vuelos;
  if ((service === 'vuelos' || service === 'oferta_externa') && context.destination) {
    return `vista del destino ${context.destination}, ${base}`;
  }
  return base;
}

export function generateAiBackgroundUrl(prompt) {
  const seed = Math.floor(Math.random() * 1_000_000_000);
  const encoded = encodeURIComponent(prompt);
  return `${POLLINATIONS_BASE}${encoded}?width=1080&height=1920&seed=${seed}&nologo=true`;
}

// ── Utilidades de imagen (mismo patrón que resizeImageFile en ClicksScreen) ──
function loadImageFromSource(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen.'));
    img.src = src;
  });
}

export function resizeImageToBlob(file, maxDim = 1600, quality = 0.88) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.onload = async () => {
      try {
        const img = await loadImageFromSource(reader.result, false);
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
          else { width = Math.round(width * maxDim / height); height = maxDim; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('No se pudo generar la imagen.'))), 'image/jpeg', quality);
      } catch (err) { reject(err); }
    };
    reader.readAsDataURL(file);
  });
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.readAsDataURL(blob);
  });
}

// Reduce un File (foto elegida por el admin) a un JPEG chico en base64, listo
// para enviar a la API de Claude -- igual que resizeImageFile en ClicksScreen.
export async function fileToBase64Jpeg(file, maxDim = 1400, quality = 0.85) {
  const blob = await resizeImageToBlob(file, maxDim, quality);
  return { base64: await blobToBase64(blob), mediaType: 'image/jpeg' };
}

export async function extractOfferFromImage({ base64, mediaType }) {
  const res = await fetch(EXTRACT_OFFER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: base64, mediaType }),
  });
  const data = await res.json().catch(() => ({}));
  return {
    origin: data.origin || null,
    destination: data.destination || null,
    airline: data.airline || null,
    price: typeof data.price === 'number' ? data.price : null,
    dates: data.dates || null,
  };
}

// Extracción simple de texto pegado (sin IA de visión). Primero intenta leer
// las etiquetas explícitas que suelen traer estas ofertas ("Origem:",
// "Destino:", "Companhia Aérea –", "A partir de R$", un mes seguido de una
// lista de días); si el texto no trae esas etiquetas, cae a heurísticas más
// genéricas. Igual de estricto que el resto del OCR: si no hay confianza en
// un dato, se deja vacío para que el admin lo complete a mano.
const LABEL_PATTERNS = {
  origin: /(?:origem|origen|from)\s*:\s*([^\n]+)/i,
  destination: /(?:destino|destination|to)\s*:\s*([^\n]+)/i,
  airline: /(?:companh(?:i|í)a\s*a[ée]rea|airline|aerol[ií]nea)\s*[-–—:]\s*([^\n]+)/i,
  price: /(?:a partir de|desde|por)\s*(?:r\$|usd|us\$|\$)\s*([\d.,]+)/i,
};

const MONTHS = 'enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setembro|octubre|outubro|noviembre|novembro|diciembre|dezembro';

const AIRLINES = ['LATAM', 'Copa Airlines', 'Copa', 'Avianca', 'Iberia', 'Air Europa', 'American Airlines', 'Gol', 'Azul', 'JetBlue', 'Wingo', 'Viva Aerobus', 'Cubana de Aviación', 'Cubana', 'TAP Portugal', 'TAP'];

function matchLabel(text, re) {
  const m = text.match(re);
  return m ? m[1].trim() : null;
}

// Recorta ruido que a veces sigue en la misma línea después de dos espacios.
function cleanPlace(raw) {
  if (!raw) return null;
  const cleaned = raw.replace(/\s{2,}.*$/, '').trim();
  return cleaned || null;
}

function extractDatesSection(text) {
  const monthBlock = text.match(new RegExp(`((?:${MONTHS})\\s*:?\\s*[\\d,\\s]+)`, 'i'));
  if (monthBlock) return monthBlock[1].trim().replace(/\s+/g, ' ');
  const dateList = text.match(/(\d{1,2}[/.]\d{1,2}(?:[/.]\d{2,4})?(?:\s*(?:,|y|-|a)\s*\d{1,2}[/.]\d{1,2}(?:[/.]\d{2,4})?)*)/);
  return dateList ? dateList[1].trim() : null;
}

export function extractOfferFromText(text) {
  const t = (text || '').trim();
  if (!t) return { origin: null, destination: null, airline: null, price: null, dates: null };

  let origin = cleanPlace(matchLabel(t, LABEL_PATTERNS.origin));
  let destination = cleanPlace(matchLabel(t, LABEL_PATTERNS.destination));

  // Sin etiquetas explícitas: intenta detectar una ruta "X ✈ Y" / "X - Y" / "X → Y".
  if (!origin || !destination) {
    const routeMatch = t.match(/([A-ZÀ-Ú][\wà-ú]{2,}(?:\s[A-ZÀ-Ú][\wà-ú]{2,})*)\s*(?:✈️|✈|→|->|—|–|-|x|X)\s*([A-ZÀ-Ú][\wà-ú]{2,}(?:\s[A-ZÀ-Ú][\wà-ú]{2,})*)/);
    if (!origin) origin = routeMatch ? routeMatch[1].trim() : null;
    if (!destination) destination = routeMatch ? routeMatch[2].trim() : null;
  }

  let airline = matchLabel(t, LABEL_PATTERNS.airline);
  if (!airline) airline = AIRLINES.find((a) => t.toLowerCase().includes(a.toLowerCase())) || null;

  let priceRaw = matchLabel(t, LABEL_PATTERNS.price);
  if (!priceRaw) {
    const generic = t.match(/(?:R\$|USD|US\$|\$)\s*([\d.,]{2,})/i);
    priceRaw = generic ? generic[1] : null;
  }
  const price = priceRaw ? parseFloat(priceRaw.replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.')) : null;

  const dates = extractDatesSection(t);

  return {
    origin: origin || null,
    destination: destination || null,
    airline: airline || null,
    price: Number.isFinite(price) && price > 0 ? price : null,
    dates: dates || null,
  };
}

export const OFFER_MARGIN_PCT = 7;
export const applyMargin = (price, marginPct = OFFER_MARGIN_PCT) =>
  Math.round(price * (1 + marginPct / 100) * 100) / 100;

// ── Datos de "Vuelos" (Mis Servicios) ─────────────────────────────────
// Hoy `input` es el texto libre que escribe el admin (ruta, aerolínea,
// precios y fechas todo junto, formato libre). El día que haya una fuente
// de datos estructurada, esta función puede recibir un objeto
// { route, airline, price, dates } y devolver los mismos `keys`, sin que el
// resto del flujo (revisión, composición, compartir) tenga que cambiar.
export function buildVuelosFields(input) {
  if (input && typeof input === 'object') {
    return [
      input.route    && { key: 'route',   label: 'Ruta',       text: input.route },
      input.airline  && { key: 'airline', label: 'Aerolínea',  text: input.airline },
      input.price    && { key: 'price',   label: 'Precio',     text: input.price },
      input.dates    && { key: 'dates',   label: 'Fechas',     text: input.dates },
    ].filter(Boolean);
  }
  const text = (input || '').trim();
  return text ? [{ key: 'vuelos_info', label: 'Información del vuelo', text }] : [];
}

// ── Utilidades de dibujo en <canvas> ───────────────────────────────────
// Respeta los saltos de línea reales del texto (cada "\n" empieza una línea
// nueva) y además envuelve por ancho dentro de cada línea/párrafo.
function wrapLines(ctx, text, maxWidth) {
  const paragraphs = String(text).split('\n');
  const lines = [];
  paragraphs.forEach((para) => {
    const words = para.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) { lines.push(''); return; }
    let current = '';
    words.forEach((word) => {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    });
    if (current) lines.push(current);
  });
  return lines;
}

function drawRoundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function withShadow(ctx, draw) {
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 6;
  draw();
  ctx.restore();
}

// ── Tarjetas de marca sólidas y opacas sobre cualquier fondo ──────────
// La legibilidad del dato es la prioridad: las tarjetas son 100% opacas
// (nunca semi-transparentes), con los dos colores de marca alternos,
// esquinas redondeadas y sombra para dar profundidad.
const CARD_NAVY = '#0B4C8C';
const CARD_ORANGE = '#F47920';
const CARD_WHITE = '#FFFFFF';
const CARD_MUTED_ON_NAVY = '#9FC1E8';
const CARD_MUTED_ON_ORANGE = '#3A2000';

function isHeroField(field) {
  const s = `${field.label} ${field.text}`.toLowerCase();
  return /precio|tasa|price|rate/.test(s);
}

function fitFontSize(ctx, text, maxWidth, startSize, minSize, weight) {
  let size = startSize;
  while (size > minSize) {
    ctx.font = `${weight} ${size}px 'Arial', sans-serif`;
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 2;
  }
  return size;
}

function drawBrandHeader(ctx, width, height, title, emoji, hasPhoto) {
  if (hasPhoto) {
    const scrim = ctx.createLinearGradient(0, 0, 0, height * 0.16);
    scrim.addColorStop(0, 'rgba(10,22,40,0.75)');
    scrim.addColorStop(1, 'rgba(10,22,40,0)');
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, width, height * 0.16);
  }

  ctx.fillStyle = CARD_ORANGE;
  ctx.fillRect(0, 0, width, height * 0.01);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = CARD_ORANGE;
  ctx.font = `800 ${height * 0.026}px 'Arial', sans-serif`;
  ctx.fillText('CLICK TRAVEL AGENCY', width / 2, height * 0.055);

  if (!title) return;
  const label = `${emoji ? `${emoji} ` : ''}${title.toUpperCase()}`;
  ctx.font = `700 ${height * 0.022}px 'Arial', sans-serif`;
  const textW = ctx.measureText(label).width;
  const pillW = textW + height * 0.06;
  const pillH = height * 0.042;
  const pillY = height * 0.1;
  ctx.fillStyle = CARD_ORANGE;
  drawRoundedRect(ctx, width / 2 - pillW / 2, pillY - pillH / 2, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.fillStyle = CARD_WHITE;
  ctx.fillText(label, width / 2, pillY);
}

function drawBrandFooter(ctx, width, height, hasPhoto) {
  if (hasPhoto) {
    const scrim = ctx.createLinearGradient(0, height * 0.9, 0, height);
    scrim.addColorStop(0, 'rgba(10,22,40,0)');
    scrim.addColorStop(1, 'rgba(10,22,40,0.6)');
    ctx.fillStyle = scrim;
    ctx.fillRect(0, height * 0.9, width, height * 0.1);
  }
  ctx.strokeStyle = CARD_ORANGE;
  ctx.lineWidth = height * 0.004;
  ctx.beginPath();
  ctx.moveTo(width * 0.32, height * 0.955);
  ctx.lineTo(width * 0.68, height * 0.955);
  ctx.stroke();
}

// Chips de icono+texto (ej. 📶 2GB  📞 15min  ✉️ 20 SMS) debajo del texto
// principal de una tarjeta -- mismo patrón que los banners de referencia.
function drawDetailChips(ctx, details, cx, cy, maxWidth, fontSize, color) {
  ctx.font = `700 ${fontSize}px 'Arial', sans-serif`;
  const chipTexts = details.map((d) => `${d.icon} ${d.text}`);
  const chipWidths = chipTexts.map((t) => ctx.measureText(t).width);
  const gap = fontSize * 1.4;
  const totalW = chipWidths.reduce((a, b) => a + b, 0) + gap * (chipTexts.length - 1);
  let x = cx - Math.min(totalW, maxWidth) / 2;
  const scale = totalW > maxWidth ? maxWidth / totalW : 1;
  ctx.textAlign = 'left';
  ctx.fillStyle = color;
  chipTexts.forEach((t, i) => {
    ctx.fillText(t, x, cy);
    x += (chipWidths[i] + gap) * scale;
  });
  ctx.textAlign = 'center';
}

// Hasta 2 campos (ej. las dos tasas de Remesas, o BRL→USD/USD→BRL): dos
// tarjetas grandes lado a lado, con la etiqueta arriba y el valor grande.
function drawStatCards(ctx, width, height, fields, top, bottom) {
  const gap = width * 0.04;
  const cardW = (width * 0.86 - gap * (fields.length - 1)) / fields.length;
  const cardH = Math.min(bottom - top, height * 0.24);
  const cardY = top + (bottom - top - cardH) / 2;
  const startX = (width - (cardW * fields.length + gap * (fields.length - 1))) / 2;

  fields.forEach((f, i) => {
    const x = startX + i * (cardW + gap);
    const onOrange = i % 2 === 1;
    withShadow(ctx, () => {
      ctx.fillStyle = onOrange ? CARD_ORANGE : CARD_NAVY;
      drawRoundedRect(ctx, x, cardY, cardW, cardH, width * 0.025);
      ctx.fill();
    });

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = onOrange ? CARD_MUTED_ON_ORANGE : CARD_MUTED_ON_NAVY;
    const capSize = height * 0.018;
    ctx.font = `700 ${capSize}px 'Arial', sans-serif`;
    wrapLines(ctx, f.label.toUpperCase(), cardW * 0.86)
      .forEach((line, li) => ctx.fillText(line, x + cardW / 2, cardY + cardH * 0.26 + li * capSize * 1.3));

    const valSize = fitFontSize(ctx, f.text, cardW * 0.82, height * 0.052, height * 0.024, '800');
    ctx.font = `800 ${valSize}px 'Arial', sans-serif`;
    ctx.fillStyle = onOrange ? CARD_NAVY : CARD_WHITE;
    const valLines = wrapLines(ctx, f.text, cardW * 0.82);
    const valLh = valSize * 1.15;
    const valStart = cardY + cardH * 0.62 - ((valLines.length - 1) * valLh) / 2;
    valLines.forEach((line, li) => ctx.fillText(line, x + cardW / 2, valStart + li * valLh));
  });
}

// 3+ campos (combos, documentos, traducciones, datos de una oferta): lista
// de tarjetas apiladas, alternando navy/naranja; la que tenga precio/tasa se
// destaca más grande -- esa es la jerarquía visual. `field.details`
// (opcional) agrega una fila de chips icono+texto (ej. Recargas).
function drawRowList(ctx, width, height, fields, top, bottom) {
  const rowGap = height * 0.014;
  const available = bottom - top;
  const rowH = Math.max(height * 0.08, Math.min(height * 0.17, (available - rowGap * (fields.length - 1)) / fields.length));
  const totalH = rowH * fields.length + rowGap * (fields.length - 1);
  const cardW = width * 0.86;
  const x = (width - cardW) / 2;
  let y = top + Math.max(0, (available - totalH) / 2);

  fields.forEach((f, i) => {
    const hero = isHeroField(f);
    const onOrange = !hero && i % 2 === 1;
    withShadow(ctx, () => {
      ctx.fillStyle = hero ? CARD_ORANGE : (onOrange ? CARD_ORANGE : CARD_NAVY);
      drawRoundedRect(ctx, x, y, cardW, rowH, width * 0.02);
      ctx.fill();
    });
    const textOnOrange = hero || onOrange;
    const mainColor = textOnOrange ? CARD_NAVY : CARD_WHITE;
    const mutedColor = textOnOrange ? CARD_MUTED_ON_ORANGE : CARD_MUTED_ON_NAVY;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const hasDetails = Array.isArray(f.details) && f.details.length > 0;
    const mainZoneH = hasDetails ? rowH * 0.6 : rowH;

    if (hero) {
      ctx.font = `700 ${height * 0.017}px 'Arial', sans-serif`;
      ctx.fillStyle = mutedColor;
      ctx.fillText(f.label.toUpperCase(), x + cardW / 2, y + mainZoneH * 0.3);

      const valSize = fitFontSize(ctx, f.text, cardW * 0.88, height * 0.044, height * 0.022, '800');
      ctx.font = `800 ${valSize}px 'Arial', sans-serif`;
      ctx.fillStyle = mainColor;
      ctx.fillText(f.text, x + cardW / 2, y + mainZoneH * 0.68);
    } else {
      const size = fitFontSize(ctx, f.text, cardW * 0.88, height * 0.026, height * 0.016, '700');
      ctx.font = `700 ${size}px 'Arial', sans-serif`;
      ctx.fillStyle = mainColor;
      const lines = wrapLines(ctx, f.text, cardW * 0.88);
      const lh = size * 1.2;
      const startLy = y + mainZoneH / 2 - ((lines.length - 1) * lh) / 2;
      lines.forEach((line, li) => ctx.fillText(line, x + cardW / 2, startLy + li * lh));
    }

    if (hasDetails) {
      drawDetailChips(ctx, f.details, x + cardW / 2, y + rowH * 0.84, cardW * 0.9, height * 0.019, mutedColor);
    }

    y += rowH + rowGap;
  });
}

// Un solo campo de texto libre con saltos de línea (ej. Vuelos pegado a
// mano): se preservan las líneas originales dentro de una sola tarjeta,
// resaltando en naranja las líneas que traen números (precios/fechas).
function drawFreeformCard(ctx, width, height, text, top, bottom) {
  const cardW = width * 0.88;
  const x = (width - cardW) / 2;
  const cardH = bottom - top;
  withShadow(ctx, () => {
    ctx.fillStyle = CARD_NAVY;
    drawRoundedRect(ctx, x, top, cardW, cardH, width * 0.03);
    ctx.fill();
  });

  const rawLines = text.split('\n').map((l) => l.trim());
  const lines = [];
  rawLines.forEach((l) => { if (l === '' && lines[lines.length - 1] === '') return; lines.push(l); });
  while (lines[0] === '') lines.shift();
  while (lines[lines.length - 1] === '') lines.pop();

  const padding = width * 0.045;
  const innerW = cardW - padding * 2;
  const innerH = cardH - padding * 2;
  let fontSize = Math.min(height * 0.032, innerH / Math.max(lines.length, 1) / 1.5);
  fontSize = Math.max(fontSize, height * 0.015);
  const lineHeight = fontSize * 1.45;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const totalH = lines.length * lineHeight;
  let cy = totalH < innerH ? top + (cardH - totalH) / 2 + lineHeight / 2 : top + padding + lineHeight / 2;

  lines.forEach((line) => {
    if (line === '') { cy += lineHeight * 0.5; return; }
    const hasDigit = /\d/.test(line);
    ctx.fillStyle = hasDigit ? CARD_ORANGE : CARD_WHITE;
    const weight = hasDigit ? '800' : '700';
    const size = hasDigit ? fontSize * 1.05 : fontSize;
    ctx.font = `${weight} ${size}px 'Arial', sans-serif`;
    wrapLines(ctx, line, innerW).forEach((wrapped) => {
      ctx.fillText(wrapped, x + cardW / 2, cy);
      cy += lineHeight;
    });
  });
}

// Punto de entrada único de composición: dibuja el fondo (foto generada con
// IA, foto propia del admin, o un degradado de marca si no hay ninguna) y
// encima las tarjetas de datos, eligiendo automáticamente el layout (dos
// tarjetas, lista de tarjetas, o tarjeta de texto libre) según los campos.
export async function compositeCardDesign({ fields, format, title, emoji, backgroundUrl }) {
  const { width, height } = FORMATS[format] || FORMATS.square;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  let hasPhoto = false;
  if (backgroundUrl) {
    try {
      const img = await loadImageFromSource(backgroundUrl, true);
      const scale = Math.max(width / img.width, height / img.height);
      const drawW = img.width * scale;
      const drawH = img.height * scale;
      ctx.drawImage(img, (width - drawW) / 2, (height - drawH) / 2, drawW, drawH);
      hasPhoto = true;
    } catch { /* si falla la carga, cae al degradado de marca */ }
  }
  if (!hasPhoto) {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#0F1E3A');
    gradient.addColorStop(1, '#0A1628');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  drawBrandHeader(ctx, width, height, title, emoji, hasPhoto);
  drawBrandFooter(ctx, width, height, hasPhoto);

  const contentTop = height * 0.17;
  const contentBottom = height * 0.92;
  const usableFields = fields.filter((f) => f.text);

  if (usableFields.length === 1 && usableFields[0].text.includes('\n')) {
    drawFreeformCard(ctx, width, height, usableFields[0].text, contentTop, contentBottom);
  } else if (usableFields.length > 0 && usableFields.length <= 2) {
    drawStatCards(ctx, width, height, usableFields, contentTop, contentBottom);
  } else if (usableFields.length > 2) {
    drawRowList(ctx, width, height, usableFields, contentTop, contentBottom);
  }

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
}

// ── Compartir (Web Share API con archivos) ────────────────────────────
export async function shareGeneratedImages(blobsByFormat) {
  const files = Object.entries(blobsByFormat).map(([format, blob], i) =>
    new File([blob], `click-travel-${format}-${Date.now()}-${i}.jpg`, { type: 'image/jpeg' }));

  if (navigator.canShare && navigator.canShare({ files })) {
    await navigator.share({ files });
    return true;
  }
  return false;
}
